/**
 * What Roger says about a clinician nobody has signed in as (EMR-1264).
 *
 * Two surfaces already show these names, and Add Episode is now a third. The
 * treatment is the same everywhere; the explanation is not, so the wording
 * lives here once and each surface names the moment it is in.
 *
 * - `assignment` — the reader is looking at work already done and wants to
 *   know what to do with it.
 * - `directory` — the User Manager, where the reader administers the row
 *   itself and wants to know why it is here and what clears it.
 * - `carry` — the reader is handing an imported chart's existing assignment
 *   forward to a new episode, which Roger allows (EMR-1728), so the copy
 *   offers the choice rather than refusing it.
 */
export type UnclaimedClinicianVariant = 'assignment' | 'directory' | 'carry';

const COPY: Record<
  UnclaimedClinicianVariant,
  { label: (name: string) => string; lines: string[] }
> = {
  assignment: {
    label: name => `Why ${name} cannot be assigned new work`,
    lines: [
      'This clinician was imported from Kinnser.',
      'They have no Roger account for this clinic yet.',
      'Reassign the work, or ask them to create their account.',
    ],
  },
  carry: {
    label: name => `Why ${name} is marked imported`,
    lines: [
      'This person was imported from Kinnser.',
      'They have no Roger account for this clinic yet.',
      'Keep them, or choose someone with an account.',
    ],
  },
  directory: {
    label: name => `Why ${name} is not in assignment lists`,
    lines: [
      'This member was imported from Kinnser.',
      'Nobody has signed in to the account yet.',
      'Roger keeps them out of assignment lists until someone does.',
    ],
  },
};

/**
 * The popover label and lines for one variant.
 *
 * `UnclaimedClinicianName` marks the name. A surface that marks something
 * else — Add Episode marks the *field*, because a Radix select trigger is a
 * button and the marker's own button cannot sit inside it — reads the copy
 * from here so there is still only one copy.
 */
export function unclaimedClinicianCopy(
  variant: UnclaimedClinicianVariant,
  name: string
): { popoverLabel: string; lines: string[] } {
  const copy = COPY[variant];
  return { popoverLabel: copy.label(name), lines: copy.lines };
}
